import { useEffect, useRef, useState, useCallback } from "react";

const ICE = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];
const API = process.env.REACT_APP_BACKEND_URL;
let iceCache = null;
async function fetchIce() {
  if (iceCache) return iceCache;
  try { const r = await fetch(`${API}/api/public/ice-servers`); const d = await r.json(); iceCache = d; return d; }
  catch { return { iceServers: ICE, turn: false }; }
}

// Minimal 1:1 WebRTC over the backend WebSocket relay. Clinician is the offerer.
export function useWebRTC({ roomKey, role, enabled = true }) {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [peerOnline, setPeerOnline] = useState(false);
  const [camError, setCamError] = useState("");
  const [events, setEvents] = useState(0);
  const [lastMessage, setLastMessage] = useState(null);
  const [relay, setRelay] = useState(null);
  const ice = useRef(ICE);
  useEffect(() => { fetchIce().then((d) => { ice.current = d.iceServers; setRelay(d.turn); }); }, []);
  const pc = useRef(null);
  const ws = useRef(null);
  const local = useRef(null);

  const send = useCallback((m) => { if (ws.current?.readyState === 1) ws.current.send(JSON.stringify(m)); }, []);

  const makePc = useCallback(() => {
    const p = new RTCPeerConnection({ iceServers: ice.current });
    local.current?.getTracks().forEach((t) => p.addTrack(t, local.current));
    p.onicecandidate = (e) => e.candidate && send({ type: "ice", candidate: e.candidate });
    p.ontrack = (e) => setRemoteStream(e.streams[0]);
    pc.current = p;
    return p;
  }, [send]);

  useEffect(() => {
    if (!enabled || !roomKey) return undefined;
    let closed = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 1280 } }, audio: true });
        if (closed) { s.getTracks().forEach((t) => t.stop()); return; }
        local.current = s; setLocalStream(s);
      } catch { setCamError("Camera or microphone unavailable. Check permissions."); }
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
      const sock = new WebSocket(`${base}/api/ws/room/${roomKey}?role=${role}`);
      ws.current = sock;
      sock.onmessage = async (ev) => {
        const m = JSON.parse(ev.data);
        setLastMessage({ ...m, _t: Date.now() });
        if (m.type === "peer_joined") {
          setPeerOnline(true);
          if (role === "clinician") {
            const p = makePc();
            const offer = await p.createOffer();
            await p.setLocalDescription(offer);
            send({ type: "offer", sdp: offer });
          }
        } else if (m.type === "offer") {
          const p = makePc();
          await p.setRemoteDescription(m.sdp);
          const ans = await p.createAnswer();
          await p.setLocalDescription(ans);
          send({ type: "answer", sdp: ans });
          setPeerOnline(true);
        } else if (m.type === "answer") {
          await pc.current?.setRemoteDescription(m.sdp);
        } else if (m.type === "ice") {
          try { await pc.current?.addIceCandidate(m.candidate); } catch {}
        } else if (m.type === "peer_left") {
          setPeerOnline(false); setRemoteStream(null); pc.current?.close(); pc.current = null;
        } else if (m.type === "evidence_updated") {
          setEvents((n) => n + 1);
        }
      };
    })();
    return () => {
      closed = true;
      ws.current?.close(); pc.current?.close();
      local.current?.getTracks().forEach((t) => t.stop());
    };
  }, [roomKey, role, enabled, makePc, send]);

  return { localStream, remoteStream, peerOnline, camError, send, events, lastMessage, relay };
}

export function snapshot(videoEl) {
  if (!videoEl || !videoEl.videoWidth) return null;
  const c = document.createElement("canvas");
  c.width = videoEl.videoWidth; c.height = videoEl.videoHeight;
  c.getContext("2d").drawImage(videoEl, 0, 0);
  return c.toDataURL("image/jpeg", 0.85);
}
